import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  ArrowUpRight,
  Atom,
  BookOpen,
  CircleUserRound,
  Home,
  Menu,
  Palette,
  Settings,
  User,
  X,
  type LucideIcon,
} from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";
import "./AppNavigation.css";

interface NavigationLink {
  label: string;
  to: string;
  icon: LucideIcon;
}

interface NavigationCard {
  label: string;
  tone: "learn" | "you" | "account";
  links: NavigationLink[];
}

const navigationCards: NavigationCard[] = [
  {
    label: "Learn",
    tone: "learn",
    links: [
      { label: "Home", to: "/home", icon: Home },
      { label: "Courses", to: "/learning", icon: BookOpen },
    ],
  },
  {
    label: "You",
    tone: "you",
    links: [
      { label: "Profile", to: "/profile", icon: User },
      { label: "Customize atom", to: "/avatar", icon: Palette },
    ],
  },
  {
    label: "Account",
    tone: "account",
    links: [
      { label: "Settings", to: "/settings", icon: Settings },
      { label: "Account information", to: "/settings/account", icon: CircleUserRound },
    ],
  },
];

const desktopLinks = [
  { label: "Home", to: "/home", icon: Home },
  { label: "Profile", to: "/profile", icon: User },
  { label: "Settings", to: "/settings", icon: Settings },
];

const navigationLinksBySpecificity = navigationCards
  .flatMap((card) => card.links)
  .sort((left, right) => right.to.length - left.to.length);

function MobileCardNav() {
  const { pathname } = useLocation();
  const prefersReducedMotion = useReducedMotion();
  const [isOpen, setIsOpen] = useState(false);

  const currentLink = navigationLinksBySpecificity.find(
    (link) => pathname === link.to || pathname.startsWith(`${link.to}/`),
  );
  const currentLabel = pathname.startsWith("/lesson/") ? "Lesson" : currentLink?.label ?? "Learn";

  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const duration = prefersReducedMotion ? 0 : 0.28;

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="scico-app-nav__backdrop"
            aria-hidden="true"
            onClick={() => setIsOpen(false)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration }}
          />
        )}
      </AnimatePresence>

      <nav className={`scico-app-nav__mobile ${isOpen ? "is-open" : ""}`} aria-label="Primary navigation">
        <div className="scico-app-nav__bar">
          <div className="scico-app-nav__identity" aria-label={`Scico, ${currentLabel}`}>
            <span className="scico-app-nav__mark" aria-hidden="true">
              <Atom />
            </span>
            <span className="scico-app-nav__identity-copy">
              <strong>Scico</strong>
              <span>{currentLabel}</span>
            </span>
          </div>

          <button
            type="button"
            className="scico-app-nav__menu-button"
            aria-label={isOpen ? "Close navigation" : "Open navigation"}
            aria-expanded={isOpen}
            aria-controls="mobile-navigation-links"
            onClick={() => setIsOpen((open) => !open)}
          >
            {isOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
          </button>
        </div>

        <AnimatePresence initial={false}>
          {isOpen && (
            <motion.div
              id="mobile-navigation-links"
              className="scico-app-nav__content"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="scico-app-nav__cards">
                {navigationCards.map((card, index) => (
                  <motion.section
                    key={card.label}
                    className={`scico-app-nav__card scico-app-nav__card--${card.tone}`}
                    aria-labelledby={`mobile-nav-${card.tone}`}
                    initial={{ y: prefersReducedMotion ? 0 : 18, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{
                      duration,
                      delay: prefersReducedMotion ? 0 : 0.04 + index * 0.06,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                  >
                    <h2 id={`mobile-nav-${card.tone}`} className="scico-app-nav__card-label">
                      {card.label}
                    </h2>
                    <div className="scico-app-nav__card-links">
                      {card.links.map((link) => {
                        const Icon = link.icon;
                        return (
                          <NavLink
                            key={link.to}
                            to={link.to}
                            end={link.to !== "/learning"}
                            className={({ isActive }) =>
                              `scico-app-nav__card-link ${isActive ? "is-active" : ""}`
                            }
                          >
                            <Icon aria-hidden="true" />
                            <span>{link.label}</span>
                            <ArrowUpRight className="scico-app-nav__link-arrow" aria-hidden="true" />
                          </NavLink>
                        );
                      })}
                    </div>
                  </motion.section>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </>
  );
}

function DesktopBottomNav() {
  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 hidden h-16 border-t bg-background md:block" aria-label="Primary navigation">
      <div className="flex h-full">
        {desktopLinks.map((link) => {
          const Icon = link.icon;
          return (
            <NavLink
              key={link.to}
              to={link.to}
              aria-label={link.label}
              className={({ isActive }) =>
                `flex flex-1 flex-col items-center justify-center gap-1 text-xs font-medium transition-colors ${
                  isActive ? "text-primary" : "text-muted-foreground"
                }`
              }
            >
              <Icon className="h-5 w-5" aria-hidden="true" />
              <span>{link.label}</span>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}

export default function AppNavigation() {
  return (
    <>
      <MobileCardNav />
      <DesktopBottomNav />
    </>
  );
}
